import React, { useState, useEffect } from "react"
import {
  Modal,
  List,
  Checkbox,
  Button,
  Space,
  Typography,
  Input,
  message,
  Tag,
} from "antd"
import {
  SettingOutlined,
  SearchOutlined,
  DragOutlined,
} from "@ant-design/icons"
import { DndContext, closestCenter } from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { userRoutes } from "routes/allRoutes"
import { getCurrentUser } from "helpers/service_helper"

const { Title, Text } = Typography
const { Search } = Input

// Sortable item component
const SortableItem = ({ id, route, isSelected, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: "8px",
  }

  return (
    <div ref={setNodeRef} style={style}>
      <List.Item
        style={{
          padding: "12px 16px",
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
          backgroundColor: isSelected ? "#e6f7ff" : "#fff",
          cursor: "move",
        }}
      >
        <Space style={{ width: "100%" }}>
          <div {...attributes} {...listeners} style={{ cursor: "grab" }}>
            <DragOutlined style={{ fontSize: "16px", color: "#8c8c8c" }} />
          </div>
          <Checkbox checked={isSelected} onChange={() => onToggle(route.id)} />
          <div style={{ flex: 1 }}>
            <Text strong>{route.title}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {route.path}
            </Text>
          </div>
          {route.icon && <Tag color="blue">{route.icon}</Tag>}
        </Space>
      </List.Item>
    </div>
  )
}

const QuickActionsManager = ({ visible, onClose, onSave, currentActions }) => {
  const [searchText, setSearchText] = useState("")
  const [selectedRoutes, setSelectedRoutes] = useState([])
  const [availableRoutes, setAvailableRoutes] = useState([])

  useEffect(() => {
    if (visible) {
      const user = getCurrentUser()
      const userRole = user?.role

      // Filter routes based on user role and hide property
      const filtered = userRoutes.filter(
        route =>
          route.roles?.includes(userRole) &&
          !route.hide &&
          route.path !== "/" &&
          route.path !== "/dashboard"
      )

      setAvailableRoutes(filtered)

      // Load current selected actions
      if (currentActions && currentActions.length > 0) {
        setSelectedRoutes(currentActions)
      } else {
        // Default selection (routes marked with isQuickAction)
        const quickActionRoutes = filtered.filter(r => r.isQuickAction)
        setSelectedRoutes(quickActionRoutes.map(r => r.id))
      }
    }
  }, [visible, currentActions])

  const handleToggleRoute = routeId => {
    setSelectedRoutes(prev => {
      if (prev.includes(routeId)) {
        return prev.filter(id => id !== routeId)
      } else {
        if (prev.length >= 6) {
          message.warning("حداکثر 6 دسترسی سریع می‌توانید انتخاب کنید")
          return prev
        }
        return [...prev, routeId]
      }
    })
  }

  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      setSelectedRoutes(items => {
        const oldIndex = items.indexOf(active.id)
        const newIndex = items.indexOf(over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleSave = () => {
    if (selectedRoutes.length === 0) {
      message.warning("لطفا حداقل یک مورد را انتخاب کنید")
      return
    }
    onSave(selectedRoutes)
    message.success("تنظیمات ذخیره شد")
    onClose()
  }

  const handleReset = () => {
    const quickActionRoutes = availableRoutes.filter(r => r.isQuickAction)
    setSelectedRoutes(quickActionRoutes.map(r => r.id))
    message.info("تنظیمات به حالت پیش‌فرض بازگشت")
  }

  // Filter routes for search
  const filteredRoutes = availableRoutes.filter(route =>
    route.title.toLowerCase().includes(searchText.toLowerCase())
  )

  // Get selected route objects in order
  const selectedRouteObjects = selectedRoutes
    .map(id => availableRoutes.find(r => r.id === id))
    .filter(Boolean)

  // Get unselected routes
  const unselectedRoutes = filteredRoutes.filter(
    route => !selectedRoutes.includes(route.id)
  )

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>مدیریت دسترسی سریع</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="reset" onClick={handleReset}>
          بازگشت به پیش‌فرض
        </Button>,
        <Button key="cancel" onClick={onClose}>
          انصراف
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          ذخیره تنظیمات
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Instructions */}
        <div
          style={{
            padding: "12px",
            background: "#f0f5ff",
            borderRadius: "8px",
          }}
        >
          <Text>
            • دسترسی‌های مورد نظر را انتخاب کنید (حداکثر 6 مورد)
            <br />
            • برای تغییر ترتیب، آیتم‌ها را بکشید و رها کنید
            <br />• انتخاب‌های شما در مرورگر ذخیره می‌شود
          </Text>
        </div>

        {/* Search */}
        <Search
          placeholder="جستجوی دسترسی..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
        />

        {/* Selected Items (Sortable) */}
        {selectedRouteObjects.length > 0 && (
          <div>
            <Title level={5}>
              دسترسی‌های انتخاب شده ({selectedRouteObjects.length}/6)
            </Title>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedRoutes}
                strategy={verticalListSortingStrategy}
              >
                {selectedRouteObjects.map(route => (
                  <SortableItem
                    key={route.id}
                    id={route.id}
                    route={route}
                    isSelected={true}
                    onToggle={handleToggleRoute}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Available Items */}
        {unselectedRoutes.length > 0 && (
          <div>
            <Title level={5}>دسترسی‌های موجود</Title>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <List
                dataSource={unselectedRoutes}
                renderItem={route => (
                  <List.Item
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleToggleRoute(route.id)}
                  >
                    <Space style={{ width: "100%" }}>
                      <Checkbox checked={false} />
                      <div style={{ flex: 1 }}>
                        <Text strong>{route.title}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {route.path}
                        </Text>
                      </div>
                      {route.icon && <Tag color="default">{route.icon}</Tag>}
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}
      </Space>
    </Modal>
  )
}

export default QuickActionsManager
